import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { CalendarOccurrence } from '../../types';
import { requestOpenCalendarAt, requestOpenDetail, requestOpenEditor, useCalendarRange } from '../../services/calendarService';
import { formatDateKo, itemsByDay, keyParts, monthGridKeys, monthGridRange, monthLabel, pad2, shiftMonth, todayKey } from '../../utils/calendarDate';
import { MonthGrid } from '../calendar/MonthGrid';
import { AgendaList } from '../calendar/AgendaList';
import { readFilters } from '../calendar/scopeStyles';

interface MiniCalendarCardProps {
  /** '전체 보기' — 캘린더 탭으로 */
  onOpenCalendar?: () => void;
}

const MAX_ITEMS = 4;

/**
 * 홈 미니 캘린더(넓은 홈 오른쪽 단 · 한 줄 배치 공용) — 이번 달 격자(일정 있는 날에 점) + 고른 날(기본 오늘)의 일정.
 *
 * 데이터는 calendarService.useCalendarRange(월 격자 42일)로 카드가 직접 받는다. 데스크톱 목록 패널과 모바일에 두 벌
 * 마운트돼도 모듈 캐시라 요청은 1건이고, 캘린더 탭과 같은 키라 탭을 열어도 다시 받지 않는다.
 * 일정을 누르면 상세 창만 연다(홈을 떠나지 않는다) — 상세의 '캘린더에서 보기'가 탭으로 간다.
 * 분류 필터는 캘린더 탭에서 고른 값(localStorage)을 그대로 따른다.
 */
export const MiniCalendarCard: React.FC<MiniCalendarCardProps> = React.memo(({ onOpenCalendar }) => {
  const today = todayKey();
  const [cursor, setCursor] = useState(() => { const p = keyParts(today); return { y: p.y, m: p.m }; });
  const [selected, setSelected] = useState(today);
  const gridKeys = useMemo(() => monthGridKeys(cursor.y, cursor.m), [cursor.y, cursor.m]);
  const range = useMemo(() => monthGridRange(cursor.y, cursor.m), [cursor.y, cursor.m]);
  const { data, loading, error, refresh } = useCalendarRange(range.from, range.to);
  const filters = readFilters();

  const occurrences = useMemo(
    () => (data?.occurrences || []).filter((o: CalendarOccurrence) => filters[o.source === 'intranet' ? 'dept' : o.scope]),
    // filters는 렌더마다 localStorage에서 읽는 원시 값 묶음 — 문자열로 비교한다
    [data, JSON.stringify(filters)], // eslint-disable-line
  );
  const byDay = useMemo(() => itemsByDay(occurrences, range.from, range.to), [occurrences, range.from, range.to]);

  const goMonth = (delta: number) => {
    const next = shiftMonth(cursor.y, cursor.m, delta);
    const t = keyParts(today);
    setCursor(next);
    setSelected(t.y === next.y && t.m === next.m ? today : `${next.y}-${pad2(next.m)}-01`);
  };

  const openOcc = (o: CalendarOccurrence) => requestOpenDetail({ eventId: o.eventId, date: o.startDate });

  return (
    <section className="bg-white rounded-2xl shadow-sm border border-slate-100 px-3 pt-3 pb-3" aria-label="일정">
      <div className="flex items-center justify-between gap-1 mb-1 pl-1.5">
        <div className="flex items-center gap-0.5 min-w-0">
          <h2 className="text-sm font-bold text-slate-600 mr-1">일정</h2>
          <button type="button" onClick={() => goMonth(-1)} aria-label="이전 달" className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100">
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <span className="text-[13px] font-semibold text-slate-700 tabular-nums whitespace-nowrap">{monthLabel(cursor.y, cursor.m)}</span>
          <button type="button" onClick={() => goMonth(1)} aria-label="다음 달" className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100">
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            onClick={() => requestOpenEditor({ date: selected })}
            aria-label="일정 추가"
            title="일정 추가"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-100"
          >
            <Plus size={17} aria-hidden="true" />
          </button>
          {onOpenCalendar && (
            <button
              type="button"
              onClick={onOpenCalendar}
              className="h-8 pl-2.5 pr-1.5 shrink-0 rounded-lg text-[13px] font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-800 flex items-center gap-0.5 transition-colors"
            >
              전체 보기
              <ChevronRight size={15} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {error && !data ? (
        <div className="px-1.5 py-2">
          <p className="text-sm font-semibold text-slate-700">일정을 불러오지 못했습니다</p>
          <button type="button" onClick={refresh} className="mt-2 h-8 px-2.5 rounded-lg border border-slate-200 text-[13px] font-semibold text-slate-600 hover:bg-slate-50">
            다시 시도
          </button>
        </div>
      ) : (
        <>
          <div className={loading && !data ? 'opacity-60' : ''}>
            <MonthGrid
              gridKeys={gridKeys}
              month={cursor.m}
              occurrences={occurrences}
              holidays={data?.holidays || {}}
              today={today}
              selected={selected}
              onSelectDay={setSelected}
              variant="dots"
              dense
            />
          </div>
          <div className="mt-1.5 pt-2 border-t border-slate-100">
            <p className="px-1.5 pb-0.5 text-[13px] font-bold text-slate-600">
              {selected === today ? '오늘' : formatDateKo(selected)}
              {data?.holidays?.[selected] && <span className="ml-1.5 font-medium text-slate-500">{data.holidays[selected]}</span>}
            </p>
            {loading && !data ? (
              <div className="px-1.5 py-2 space-y-1.5" aria-busy="true">
                <div className="h-4 w-3/4 rounded bg-slate-100 animate-pulse" />
              </div>
            ) : (
              <AgendaList
                days={[selected]}
                byDay={byDay}
                holidays={data?.holidays || {}}
                today={today}
                onOpen={openOcc}
                showEmptyDays
                hideDayHeader
                compact
                maxItemsPerDay={MAX_ITEMS}
                onMore={(day) => requestOpenCalendarAt({ date: day })}
                emptyTitle={selected === today ? '오늘 일정이 없습니다' : '이 날 일정이 없습니다'}
                emptyBody="캘린더에서 일정을 추가해 보세요"
              />
            )}
          </div>
        </>
      )}
    </section>
  );
});
MiniCalendarCard.displayName = 'MiniCalendarCard';
